import React, { ReactNode, useRef } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { Reveal } from './Reveal';
import { SplitText } from './SplitText';
import { MagneticButton } from './MagneticButton';
import { useContent } from '../content/ContentProvider';
import '../styles/glass.css';

const emReveal = {
  hidden: { opacity: 0, y: '0.4em', filter: 'blur(10px)' },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.8, delay: 0.55, ease: [0.2, 0.7, 0.2, 1] as any },
  },
};

interface HeaderProps {
  property?: 'mobile' | 'desktop';
  children?: ReactNode;
}

const scrollTo = (id: string) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export const Header: React.FC<HeaderProps> = ({ children }) => {
  const { hero, links } = useContent();
  const sectionRef = useRef<HTMLElement | null>(null);
  const reduce = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });

  const titleY = useTransform(scrollYProgress, [0, 1], ['0%', reduce ? '0%' : '-22%']);
  const metaY = useTransform(scrollYProgress, [0, 1], ['0%', reduce ? '0%' : '-8%']);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, reduce ? 1 : 0.4]);

  return (
    <section className="hero" id="top" ref={sectionRef as any}>
      <div className="hero-content">
        {hero.badge && (
          <Reveal>
            <span className="hero-badge">{hero.badge}</span>
          </Reveal>
        )}

        <Reveal className="hero-tag-row">
          {hero.chips.map((chip) => (
            <span key={chip} className="chip">{chip}</span>
          ))}
        </Reveal>

        <motion.div className="hero-title glass-strong" style={{ y: titleY, opacity }}>
          <h1 className="display-h1">
            <SplitText text={hero.titleLine1} />
            <br />
            <motion.em
              variants={emReveal}
              initial={reduce ? 'visible' : 'hidden'}
              animate="visible"
              style={{ display: 'inline-block', fontStyle: 'normal' }}
            >
              {hero.titleLine2}
            </motion.em>
          </h1>
        </motion.div>

        <motion.div className="hero-meta" style={{ y: metaY }}>
          <Reveal delay={400}>
            <p className="lead" style={{ maxWidth: 520 }}>
              {hero.lead}
            </p>
          </Reveal>
          <Reveal delay={500}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <MagneticButton as="a" className="btn-glass primary" href={links.calendly} target="_blank" rel="noreferrer">
                {hero.primaryCta}
              </MagneticButton>
              <MagneticButton as="button" className="btn-glass" onClick={() => scrollTo('portfolio')}>
                {hero.secondaryCta}
              </MagneticButton>
            </div>
          </Reveal>
        </motion.div>
      </div>

      {hero.photo && (
        <Reveal delay={250} className="hero-photo">
          <img src={`${process.env.PUBLIC_URL}${hero.photo}`} alt={hero.photoAlt} />
        </Reveal>
      )}

      {children}
    </section>
  );
};
